import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check, ArrowRight, X, Youtube } from "lucide-react";
import { trackEvent } from "@/components/Analytics";
import EmailComposeButtons from "@/components/EmailComposeButtons";
import { Link } from "react-router-dom";

/**
 * Instant Onboarding: fires on first Dashboard mount when user has YouTube connected
 * but hasn't been onboarded yet. Runs one Claude call to score fit against a top brand,
 * drafts a pitch email, and drops a sponsor into the user's Kanban.
 */
export default function OnboardingModal() {
  const [status, setStatus] = useState(null); // { onboarded, has_youtube, ... }
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // sponsor doc
  const [open, setOpen] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/onboarding/status").then(({ data }) => {
      if (cancelled) return;
      setStatus(data);
      // If user is already onboarded and there's a stored lead, don't auto-open — dashboard shows via sponsor list
      // If user hasn't been onboarded but has YouTube → auto-open and run
      if (!data.onboarded && data.has_youtube) {
        setOpen(true);
        runOnboarding();
      } else if (!data.onboarded && !data.has_youtube) {
        // Show a lightweight "connect YouTube" nudge modal
        setOpen(true);
      }
    }).catch((e) => console.error("onboarding/status failed", e));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runOnboarding = async () => {
    setRunning(true);
    trackEvent("onboarding_run_started", {});
    try {
      const { data } = await api.post("/onboarding/run");
      setResult(data.sponsor);
      trackEvent("onboarding_run_completed", {
        brand: data.sponsor?.brand,
        score: data.sponsor?.fit_score,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate your first lead. Try again from Sponsors.");
      setOpen(false);
    } finally {
      setRunning(false);
    }
  };

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/onboarding/dismiss");
      trackEvent("onboarding_dismissed", {});
    } catch (e) {
      console.error("dismiss failed", e);
    } finally {
      setOpen(false);
      setDismissing(false);
    }
  };

  const copyField = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
    } catch (e) {
      console.error("copy failed", e);
    }
  };

  if (!open || !status) return null;

  const verdictColor = (v) => v === "strong-fit" ? "#34C759" : v === "decent-fit" ? "#FFC107" : v === "weak-fit" ? "#FF9500" : "#FF3B30";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 sd-rise" data-testid="onboarding-modal">
      <div className="w-full max-w-2xl sd-card p-6 md:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="sd-label mb-2 text-[#FF3B30]">● 60-SECOND ONBOARDING</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              {status.has_youtube ? "Your first sponsor lead" : "Connect YouTube to unlock this"}
            </h2>
          </div>
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="sd-btn sd-btn-ghost h-9 w-9 p-0 shrink-0"
            data-testid="onboarding-close-btn"
            aria-label="Close"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>

        {/* No YouTube — show connect nudge */}
        {!status.has_youtube && (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm leading-relaxed">
              We can auto-generate your first sponsor lead — brand fit score + a ready-to-send pitch email — the moment you connect YouTube. Takes 20 seconds. Read-only access.
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <Link
                to="/analytics"
                onClick={() => setOpen(false)}
                className="sd-btn sd-btn-accent h-12 flex-1"
                data-testid="onboarding-connect-yt-btn"
              >
                <Youtube size={14} strokeWidth={1.5} /> Connect YouTube <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <button
                onClick={dismiss}
                disabled={dismissing}
                className="sd-btn sd-btn-ghost h-12 md:w-40"
                data-testid="onboarding-skip-btn"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* YouTube connected — running or result */}
        {status.has_youtube && running && (
          <div className="py-10 text-center">
            <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30] mb-4" />
            <div className="font-mono text-sm text-zinc-400">Scanning your channel and picking a brand match…</div>
            <div className="mt-2 text-xs text-zinc-500">This takes 15-25 seconds. Do not close.</div>
          </div>
        )}

        {status.has_youtube && !running && result && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
              <div
                className="font-display text-7xl leading-none tabular-nums"
                style={{ color: verdictColor(result.fit_verdict) }}
                data-testid="onboarding-score-value"
              >
                {result.fit_score}<span className="text-xl text-zinc-500">/100</span>
              </div>
              <div>
                <div className="sd-label mb-1">MATCHED BRAND</div>
                <div className="font-display text-2xl mb-2">{result.brand}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: verdictColor(result.fit_verdict) }}>
                  ● {(result.fit_verdict || "").replace("-", " ")}
                </div>
              </div>
            </div>

            {result.fit_alignment && (
              <p className="text-sm text-zinc-300 leading-relaxed border-l-2 border-[#27272a] pl-4">
                {result.fit_alignment}
              </p>
            )}

            {result.draft_pitch_email && (
              <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="sd-label text-[#FF3B30]">DRAFT PITCH EMAIL</div>
                  <button
                    onClick={() => copyField("email", result.draft_pitch_email)}
                    className="sd-btn sd-btn-ghost h-8 px-2 text-xs"
                    data-testid="onboarding-copy-email-btn"
                  >
                    {copiedField === "email" ? <><Check size={12} strokeWidth={1.5} /> Copied</> : <><Copy size={12} strokeWidth={1.5} /> Copy</>}
                  </button>
                </div>
                <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
                  {result.draft_pitch_email}
                </pre>
                <div className="mt-3">
                  <EmailComposeButtons
                    email={result.draft_pitch_email}
                    testIdPrefix="onboarding-compose"
                  />
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-[#27272a]">
              <p className="text-xs text-zinc-500 mb-4 font-mono">
                ● Added to your Sponsors board in "PITCHED" stage. Edit or delete anytime.
              </p>
              <div className="flex flex-col md:flex-row gap-3">
                <Link
                  to="/sponsors"
                  onClick={() => setOpen(false)}
                  className="sd-btn sd-btn-accent h-11 flex-1"
                  data-testid="onboarding-view-sponsor-btn"
                >
                  <Sparkles size={14} strokeWidth={1.5} /> Open in Sponsors <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="sd-btn sd-btn-ghost h-11 md:w-40"
                  data-testid="onboarding-done-btn"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
